export type OfacLanguage = 'en' | 'es';

export const OFAC_GL_52C_EFFECTIVE = '2026-09-14';

export const OFAC_VENEZUELA_WINDOW_SOURCES = [
  {
    id: 'gl52c',
    labelEn: 'OFAC General License 52C (PdVSA)',
    labelEs: 'Licencia General 52C de OFAC (PdVSA)',
    href: 'https://ofac.treasury.gov/media/936926/download?inline=',
  },
  {
    id: 'venezuela-program',
    labelEn: 'OFAC Venezuela-related sanctions',
    labelEs: 'Sanciones de OFAC relacionadas con Venezuela',
    href: 'https://ofac.treasury.gov/sanctions-programs-and-country-information/venezuela-related-sanctions',
  },
  {
    id: 'selected-gls',
    labelEn: 'OFAC selected general licenses',
    labelEs: 'Licencias generales seleccionadas de OFAC',
    href: 'https://ofac.treasury.gov/selected-general-licenses-issued-ofac',
  },
  {
    id: 'proyetech',
    labelEn: 'Market briefing: Venezuela business window (Proyetech)',
    labelEs: 'Informe de mercado: ventana de negocios en Venezuela (Proyetech)',
    href: 'https://www.linkedin.com/pulse/venezuela-ante-una-nueva-ventana-de-negocios-c%C3%B3mo-organizarse-j266f/',
  },
] as const;

export const OFAC_VENEZUELA_FLOW_LINKS = [
  {
    id: 'sdn-license',
    to: '/products/ofac-compliance/sdn-and-general-license-screening',
    labelEn: 'SDN and general license screening',
    labelEs: 'Screening SDN y licencias generales',
    mapsTo: 'opportunity' as const,
  },
  {
    id: 'capta',
    to: '/products/ofac-compliance/consolidated-and-capta-list-coverage',
    labelEn: 'CAPTA and consolidated lists',
    labelEs: 'Listas CAPTA y consolidadas',
    mapsTo: 'opportunity' as const,
  },
  {
    id: 'monitoring',
    to: '/products/ofac-compliance/continuous-ofac-monitoring',
    labelEn: 'Continuous OFAC monitoring',
    labelEs: 'Monitoreo OFAC continuo',
    mapsTo: 'movement' as const,
  },
  {
    id: 'realtime-batch',
    to: '/products/ofac-compliance/real-time-and-batch-ofac-checks',
    labelEn: 'Real-time and batch checks',
    labelEs: 'Chequeos en tiempo real y por lote',
    mapsTo: 'business' as const,
  },
] as const;

type CopyBlock = { en: string; es: string };

export const OFAC_VENEZUELA_WINDOW_COPY = {
  kicker: {
    en: 'Current license context',
    es: 'Contexto de licencia vigente',
  },
  title: {
    en: 'Venezuela window: GL 52C is activity, not a skip of OFAC controls',
    es: 'Ventana Venezuela: la GL 52C es actividad, no un atajo de controles OFAC',
  },
  help: {
    en: 'A new general license is a movement. NextCompli still screens the party, tests whether the activity is in scope, and keeps payment, reporting, and evidence with the decision. This briefing is operating context, not legal advice.',
    es: 'Una licencia general nueva es un movimiento. NextCompli sigue cribiendo a la parte, comprueba si la actividad cabe en el alcance y conserva pago, reporte y evidencia con la decisión. Este informe es contexto operativo, no asesoría legal.',
  },
  lead: {
    en: 'OFAC General License 52C, effective 14 September 2026, replaced GL 52B and authorizes certain established U.S. entity transactions involving PdVSA and 50%-owned PdVSA entities. The license still requires contracting conditions, payment routing, reporting, and SDN screening. Movement in oil, agro, or telecom is not the same as a permitted counterparty or a ready deal.',
    es: 'La Licencia General 52C de OFAC, vigente desde el 14 de septiembre de 2026, reemplazó la GL 52B y autoriza ciertas transacciones de entidades estadounidenses establecidas con PdVSA y entidades de PdVSA con 50% o más. La licencia sigue exigiendo condiciones contractuales, ruta de pago, reportes y screening SDN. Un movimiento en petróleo, agro o telecom no es lo mismo que una contraparte permitida o un negocio listo.',
  },
  licenseFactsTitle: {
    en: 'What GL 52C changes in the OFAC flow',
    es: 'Qué cambia la GL 52C en el flujo OFAC',
  },
  licenseFacts: [
    {
      en: 'Screen the party first. PdVSA remains blocked; a license can cover an activity without clearing every person, bank, or 50% affiliate.',
      es: 'Criba primero a la parte. PdVSA sigue bloqueada; una licencia puede cubrir una actividad sin autorizar a cada persona, banco o afiliada al 50%.',
    },
    {
      en: 'Keep license context next to the hit. GL 52C lets blocked PdVSA officials sign authorized contracts in an official capacity; it does not authorize Citgo governance changes.',
      es: 'Mantén el contexto de licencia junto al hit. La GL 52C permite que funcionarios bloqueados de PdVSA firmen contratos autorizados en capacidad oficial; no autoriza cambios de gobernanza de Citgo.',
    },
    {
      en: 'Build the operation around evidence: who participates, what goods or services move, how execution is documented, which payment channel is used, and what must be retained for 10-day / 90-day reports.',
      es: 'Arma la operación alrededor de evidencia: quién interviene, qué bienes o servicios se mueven, cómo se documenta la ejecución, por cuál canal se paga y qué debe conservarse para reportes a 10 y 90 días.',
    },
  ] as CopyBlock[],
  conceptsTitle: {
    en: 'Separate movement, opportunity, and business',
    es: 'Separa movimiento, oportunidad y negocio',
  },
  concepts: [
    {
      id: 'movement',
      title: { en: 'Movement', es: 'Movimiento' },
      body: {
        en: 'Something changed: GL 52C, used U.S. oilfield equipment, announced fields, or a draft telecom quality rule. Continuous monitoring and Live Feed catch that change.',
        es: 'Algo cambió: la GL 52C, equipos petroleros usados de EE.UU., campos anunciados o un proyecto de calidad en telecom. El monitoreo continuo y el feed en vivo capturan ese cambio.',
      },
    },
    {
      id: 'opportunity',
      title: { en: 'Opportunity', es: 'Oportunidad' },
      body: {
        en: 'A named entity, asset, likely need, and contracting path exist. SDN plus license screening, then CAPTA, test whether that path is even eligible.',
        es: 'Existe una entidad, un activo, una necesidad probable y una ruta de contratación. SDN más screening de licencia, y luego CAPTA, prueban si esa ruta siquiera es elegible.',
      },
    },
    {
      id: 'business',
      title: { en: 'Business', es: 'Negocio' },
      body: {
        en: 'Scope, buyer, budget, payment path, and evidence are defined. Real-time and batch checks, hire clearance, and reporting make that executable.',
        es: 'Hay alcance, comprador, presupuesto, ruta de pago y evidencia. Los chequeos en tiempo real y por lote, la autorización de contratación y los reportes lo vuelven ejecutable.',
      },
    },
  ],
  testsTitle: {
    en: 'Run compliance as an early filter',
    es: 'Usa el cumplimiento como filtro temprano',
  },
  tests: [
    {
      en: 'Identify the operator, asset, and project — “oil sector” is not a screening target.',
      es: 'Identifica al operador, el activo y el proyecto: “sector petrolero” no es un objetivo de screening.',
    },
    {
      en: 'Test license pertinence: does GL 52C or another current authorization actually cover this activity?',
      es: 'Prueba la pertinencia de la licencia: ¿la GL 52C u otra autorización vigente cubre de verdad esta actividad?',
    },
    {
      en: 'Confirm counterparties, beneficial owners, funds origin and destination, contract forum, and the payment channel before a quote.',
      es: 'Confirma contrapartes, beneficiarios, origen y destino de fondos, foro contractual y canal de pago antes de cotizar.',
    },
    {
      en: 'Keep a next verifiable step: screen now, adjudicate the hit, or document why a license applies.',
      es: 'Mantén un siguiente paso verificable: cribar ahora, adjudicar el hit o documentar por qué aplica una licencia.',
    },
  ] as CopyBlock[],
  flowTitle: {
    en: 'Continue in the OFAC flow',
    es: 'Continúa en el flujo OFAC',
  },
  demoCta: {
    en: 'Request an OFAC walkthrough',
    es: 'Solicitar un recorrido OFAC',
  },
  productCta: {
    en: 'Open OFAC Compliance',
    es: 'Abrir cumplimiento OFAC',
  },
} as const;

export function ofacVenezuelaWindowText(
  language: OfacLanguage,
  block: CopyBlock | { en: string; es: string },
): string {
  return language === 'es' ? block.es : block.en;
}

export function ofacVenezuelaSourceLabel(
  language: OfacLanguage,
  source: (typeof OFAC_VENEZUELA_WINDOW_SOURCES)[number],
): string {
  return language === 'es' ? source.labelEs : source.labelEn;
}
